import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useMemo,
} from 'react';
import { useQuery } from 'react-query';
import { telasModuloProps } from './types';
import api from '~/services/api';
import useAuth from '~/hooks/useAuth';
import icones from '~/config/icones';
import navmenu from '~/config/navmenu';

type LayoutContextData = {
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  collapsed: boolean;
  telas: TelaModuloProcessado[];
  selectedModulo: number | null;
  setSelectedModulo: React.Dispatch<React.SetStateAction<number | null>>;
  moduloSelecionado: TelaModuloProcessado | undefined;
  reloadTelas: boolean;
  setReloadTelas: React.Dispatch<React.SetStateAction<boolean>>;
};

interface TelaModuloProcessado extends Omit<telasModuloProps, 'telas'> {
  Icon?: any | null;
  telas: Array<
    Omit<telasModuloProps['telas'][number], 'content' | 'label_menu'> & {
      Icon?: any | null;
      content: React.FC | null;
      label?: string;
    }
  >;
}

export const LayoutContext = createContext({} as LayoutContextData);

interface LayoutContextProviderProps {
  children: ReactNode;
}

export function LayoutContextProvider({
  children,
}: LayoutContextProviderProps): JSX.Element {
  const user = useAuth();
  const [collapsed, setCollapsed] = useState(true);
  const [selectedModulo, setSelectedModulo] = useState<number | null>(2); // Inicia com Base
  const [reloadTelas, setReloadTelas] = useState(false);

  // Possível substituir por busca de URL no banco
  const getArrayIndexed = function (
    arr: any[],
    fieldNameKey: string,
    fieldNameValue: string,
  ) {
    const r: any = {};
    for (let i = 0; i < arr.length; i++)
      r[`${arr[i][fieldNameKey]}`] = arr[i][fieldNameValue];
    return r;
  };

  const ulrTelas = useMemo(() => getArrayIndexed(navmenu, 'id', 'url'), []);

  // Cache de telas com react-query - 15 minutos de cache
  const { data: telas = [] } = useQuery(
    ['telas-modulo', user.user?.cod_controle],
    async () => {
      if (!user.user?.cod_controle) {
        return [];
      }

      const { data } = await api.get(
        `/telas-por-modulo/${user.user.cod_controle}`,
      );

      if (!data.success) {
        throw new Error('Erro ao buscar telas');
      }

      const dataFiltered = data.data.filter((t: any) => t.telas.length > 0);

      const result: TelaModuloProcessado[] = dataFiltered.map(
        (modulo: telasModuloProps) => {
          const { lib_icon: lib_icon_modulo, des_icon: des_icon_modulo } =
            modulo;

          let moduloProcessado = modulo as telasModuloProps & {
            Icon?: any;
          };

          if (des_icon_modulo) {
            const iconkeyModulo = Object.keys(icones).find(
              (icon) => icon === lib_icon_modulo,
            );
            const LibIconModulo = iconkeyModulo ? icones[iconkeyModulo] : {};
            const IconModulo = LibIconModulo[des_icon_modulo] || null;
            moduloProcessado = {
              ...modulo,
              Icon: IconModulo,
            };
          }

          const telasProcessadas = modulo.telas
            .map((tela) => {
              const { lib_icon, des_icon } = tela;
              const iconkey = Object.keys(icones).find(
                (icon) => icon === lib_icon,
              );
              const Lib = iconkey ? icones[iconkey] : {};
              const Icon = Lib[des_icon] || null;

              const componente = navmenu.find(
                (c: any) => c.id === tela.cod_tela,
              );

              if (ulrTelas[`${tela.cod_tela}`]) {
                tela.url = ulrTelas[`${tela.cod_tela}`];
              } else {
                tela.url = '/app';
              }

              return {
                ...tela,
                Icon,
                content: componente?.content || null,
                label: tela.label_menu,
              };
            })
            .sort((a, b) => a.num_ordem_menu_secao - b.num_ordem_menu_secao);

          return {
            ...moduloProcessado,
            telas: telasProcessadas,
          };
        },
      );

      return result;
    },
    {
      enabled: !!user.user?.cod_controle,
      staleTime: 1000 * 60 * 15, // 15 minutos
      cacheTime: 1000 * 60 * 30, // 30 minutos
      refetchOnWindowFocus: false,
    },
  );

  const moduloSelecionado = telas.find((m) => m.cod_modulo === selectedModulo);

  return (
    <LayoutContext.Provider
      value={{
        collapsed,
        setCollapsed,
        telas,
        selectedModulo,
        setSelectedModulo,
        moduloSelecionado,
        reloadTelas,
        setReloadTelas,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
}

export const useLayout = (): LayoutContextData => {
  return useContext(LayoutContext);
};
